"use client";

import { useCallback, useRef } from "react";
import { motion } from "framer-motion";

import { usePortfolioEntrance } from "../../hero/parts/PortfolioSequence";
import DeckCard from "./DeckCard";
import DeckLayer from "./DeckLayer";
import useDeckPosition from "./useDeckPosition";

// Touch deck. Same stack as the desktop version, driven by a finger instead of
// the arrows: dragging left walks the deck forward, right walks it back, and the
// deck wraps, so there is no dead end in either direction. The position itself
// lives in useDeckPosition, shared with the desktop deck.
//
// The card runs the full width of the screen. It used to sit in a 440px box with
// a gutter either side, which on a phone left the deck looking like a widget on
// a page rather than the page itself.

// Past a fifth of the card's width, or a quick flick, commits to the next card.
const DISTANCE_THRESHOLD = 0.2;
const VELOCITY_THRESHOLD = 400;

export default function SwipeDeck({ clients, className = "" }) {
  const count = clients.length;
  const stageRef = useRef(null);
  const { entered, finishEntrance } = usePortfolioEntrance(stageRef);
  const { p, index: dot, positionRef, goTo, goToIndex } = useDeckPosition(count);

  const stageWidth = () => stageRef.current?.offsetWidth || 1;

  const handleDrag = useCallback(
    (_event, info) => {
      p.set(positionRef.current - info.offset.x / stageWidth());
    },
    [p],
  );

  const handleDragEnd = useCallback(
    (_event, info) => {
      const travelled = -info.offset.x / stageWidth();
      const flick = -info.velocity.x;

      let next = positionRef.current;
      if (travelled > DISTANCE_THRESHOLD || flick > VELOCITY_THRESHOLD) {
        next += 1;
      } else if (
        travelled < -DISTANCE_THRESHOLD ||
        flick < -VELOCITY_THRESHOLD
      ) {
        next -= 1;
      }
      goTo(next);
    },
    [goTo],
  );

  return (
    <div className={className}>
      {/* Two nested boxes on purpose. The outer one clips, so the card falling
          out of the deck disappears at the bottom edge instead of sliding over
          the dots and the section below — the desktop deck gets this free from
          its sticky viewport. The generous top padding sits INSIDE the clip so
          the cards riding up behind the stack are still visible; without it they
          would collide with the section heading above. */}
      {/* `isolate` for the same reason as the desktop deck: DeckLayer writes
          z-indices past 100, and in the root stacking context that is above the
          fixed navbar. */}
      <div className="isolate overflow-hidden pb-4 pt-16 sm:pt-20">
        {/* Drag lives on the stage, not on an overlay, so the links inside a
            card's glass panel stay tappable — a press that never moves is still
            delivered as a click. dragElastic 0 + zero constraints means the
            stage itself never shifts; the gesture only feeds `p`. */}
        <motion.div
          ref={stageRef}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0}
          dragMomentum={false}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          className="relative aspect-[3/4] min-h-[500px] w-full cursor-grab touch-pan-y active:cursor-grabbing"
        >
          {clients.map((client, index) => (
            <DeckLayer key={client.id} index={index} count={count} p={p} entered={entered} onEntered={finishEntrance}>
              {/* See ArrowDeck: the front card is below the fold, so it stays
                  lazy rather than preloading ahead of the hero's stylesheets. */}
              {/* Square corners now that the card reaches both edges: rounded
                  ones would leave a sliver of the page showing at each corner. */}
              <DeckCard client={client} className="h-full w-full rounded-3xl" sizes="100vw" />
            </DeckLayer>
          ))}
        </motion.div>
      </div>

      {/* Outside the clip box, so a falling card is hidden before it reaches
          them rather than sliding across them. */}
      <div className="px-4 pb-12">
        <div className="mt-4 flex items-center justify-center gap-0">
          {clients.map((client, index) => (
            <button
              key={client.id}
              type="button"
              onClick={() => goToIndex(index)}
              aria-label={client.name}
              aria-current={dot === index}
              className="flex h-11 w-6 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue"
              >
                <span aria-hidden className={`h-1.5 rounded-full transition-[width,background-color] duration-200 motion-reduce:transition-none ${dot === index ? "w-5 bg-sky-blue" : "w-1.5 bg-white/40"}`} />
              </button>
          ))}
        </div>
      </div>
    </div>
  );
}
